import { Update, type UpdateResult } from '@st/redux'
import { isMap } from '@st/util/json-value'
import { Effect } from 'effect'
import { DocumentViewerCommandMessage } from './document-viewer-command'
import { JsonStore } from './json-store'
import { ViewportService } from './viewport-service'
import type { DocumentLocation } from './page-layers'

export const VIEWPORT_SETTINGS_VERSION = '2'

export type DocumentViewerViewportState = {
  viewerShell: {
    zoom: number
    cssPxPerPdfPt: number
  }
  /**
   * Whether the zoom still tracks the fit-to-width zoom. A viewer starts each
   * document fitting (in fit-to-width mode) and stops once any other source
   * chooses the zoom: the user, their stored preference, or the host's
   * initial zoom. While it fits, every measurement that knows the first page's
   * width refits, whichever of the two arrives first. Source revisions of the
   * same document keep both the zoom and this flag.
   */
  followsFitToWidth: boolean
  viewportMeasurement: {
    containerWidthPx: number
    containerHeightPx: number
    initialZoomMode: 'actualSize' | 'fitToWidth'
    cssPxPerPdfPt: number
  } | null
}

export type DocumentViewerViewportMessage =
  | { type: 'setViewerZoom'; level: number }
  | { type: 'applyViewerZoom'; level: number }
  | { type: 'viewportSettingsLoaded'; zoom: number }
  | {
      type: 'viewportMeasured'
      containerWidthPx: number
      containerHeightPx: number
      firstPageWidthPt?: number
      initialZoomMode: 'actualSize' | 'fitToWidth'
      cssPxPerPdfPt: number
    }
  | { type: 'requestViewerScroll'; target: DocumentLocation; behavior?: ScrollBehavior }

export function reduceDocumentViewerViewportState<State extends DocumentViewerViewportState>(
  state: State,
  message: DocumentViewerViewportMessage,
  viewportSettingsKey?: string
): UpdateResult<State, DocumentViewerViewportMessage | DocumentViewerCommandMessage> | undefined {
  switch (message.type) {
    case 'setViewerZoom': {
      if (state.viewerShell.zoom == message.level) {
        return { ...state, followsFitToWidth: false }
      }

      const nextState = {
        ...state,
        viewerShell: { ...state.viewerShell, zoom: message.level },
        followsFitToWidth: false
      }
      return Update.pipe(
        nextState,
        Update.effect(() =>
          Effect.gen(function* () {
            const commandMessage = yield* emitViewerZoomChangedCommand(message.level)
            yield* persistViewportZoom(message.level, viewportSettingsKey)
            yield* pinViewportCenterAfterNextFrame()
            return commandMessage
          })
        )
      )
    }
    case 'applyViewerZoom':
      // Programmatic zoom does not change the stored user preference or
      // reposition the viewport.
      return {
        ...state,
        viewerShell: { ...state.viewerShell, zoom: message.level },
        followsFitToWidth: false
      }
    case 'viewportSettingsLoaded':
      return {
        ...state,
        viewerShell: {
          ...state.viewerShell,
          zoom: message.zoom
        },
        followsFitToWidth: false
      }
    case 'viewportMeasured':
      const shouldFitToWidth =
        message.initialZoomMode == 'fitToWidth' &&
        message.firstPageWidthPt != undefined &&
        state.followsFitToWidth
      const measuredState = {
        ...state,
        viewerShell: {
          ...state.viewerShell,
          cssPxPerPdfPt: message.cssPxPerPdfPt
        },
        viewportMeasurement: {
          containerWidthPx: message.containerWidthPx,
          containerHeightPx: message.containerHeightPx,
          initialZoomMode: message.initialZoomMode,
          cssPxPerPdfPt: message.cssPxPerPdfPt
        }
      }
      if (shouldFitToWidth && message.firstPageWidthPt != undefined) {
        return {
          ...measuredState,
          viewerShell: {
            ...measuredState.viewerShell,
            zoom: fitToWidthZoom(
              message.containerWidthPx,
              message.firstPageWidthPt,
              message.cssPxPerPdfPt
            )
          }
        }
      }
      return measuredState
    case 'requestViewerScroll':
      return state
  }
}

/** Zoom that fits a page to the container width, never enlarging past actual size. */
export function fitToWidthZoom(
  containerWidthPx: number,
  pageWidthPt: number,
  cssPxPerPdfPt: number
) {
  return Math.min(containerWidthPx / (pageWidthPt * cssPxPerPdfPt), 1)
}

function emitViewerZoomChangedCommand(level: number) {
  return Effect.succeed({
    type: 'viewerCommand',
    command: { type: 'viewer.zoom.changed', level }
  } as const)
}

export function loadViewportSettingsEffect(viewportSettingsKey: string) {
  return Effect.gen(function* () {
    const settingsStore = yield* JsonStore
    const settings = yield* settingsStore.read(viewportSettingsKey)
    if (!isMap(settings) || settings.version != VIEWPORT_SETTINGS_VERSION) {
      return null
    }

    return typeof settings.zoom == 'number'
      ? ({ type: 'viewportSettingsLoaded', zoom: settings.zoom } as const)
      : null
  })
}

function persistViewportZoom(level: number, viewportSettingsKey?: string) {
  if (!viewportSettingsKey) {
    return Effect.succeed(null)
  }

  return Effect.gen(function* () {
    const settingsStore = yield* JsonStore
    yield* settingsStore.write(viewportSettingsKey, {
      version: VIEWPORT_SETTINGS_VERSION,
      zoom: level
    })
    return null
  })
}

function pinViewportCenterAfterNextFrame() {
  return Effect.gen(function* () {
    const viewport = yield* ViewportService
    yield* viewport.pinViewportCenterAfterNextFrame()
    return null
  })
}
